<template>
    <div class="dashboard-desktop" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
        <div class="dashboard-main">
            <div class="main-container">
                <div class="hero-background">
                    <header class="dashboard-hero">
                        <div class="hero-text">
                            <p class="hero-subtitle">Hello, welcome to {{ corporateName || userInfo?.member?.corporate_name || 'membership' }} 👋</p>
                            <h1 class="hero-title">{{ memberName || dashboardView?.member_name }}</h1>
                        </div>
                    </header>
                    <section class="balance-section">
                        <!-- When Point System is NONACTIVE - Show Tier Progress -->
                        <div v-if="!isPointSystemActive" class="balance-grid balance-grid--single">
                            <div class="tier-progress-wrapper-desktop">
                                <member-tier-progress
                                    v-if="currentTierData"
                                    :state="currentTierData.state"
                                    :progress-percentage="currentTierData.progressPercentage"
                                    :current-amount="currentTierData.currentAmount"
                                    :current-tier="currentTierData.currentTierData"
                                    :next-tier="currentTierData.nextTierData"
                                    :is-selected="true"
                                />
                                <div v-else class="tier-loading-desktop">Loading tier progress...</div>
                            </div>
                        </div>
                        <!-- When Point System is ACTIVE - Show Tier, Points, and Vouchers -->
                        <div v-else class="balance-grid">
                            <article class="balance-card">
                                <div class="balance-card-icon">
                                    <img
                                        v-if="dashboardView?.tier?.icon?.image_url && !dashboardView.tier.icon.image_url.includes('fas ')"
                                        :src="dashboardView.tier.icon.image_url"
                                        alt="Tier icon"
                                    />
                                    <i v-else-if="dashboardView?.tier?.icon?.image_url?.includes('fas ') || dashboardView?.tier_name === 'Member' || !dashboardView?.tier_name || dashboardView?.tier_name === '-'" class="fas fa-medal fa-2x medal-icon-desktop"></i>
                                    <img v-else src="/img/member-icon/tier-logo.svg" alt="Tier icon" />
                                </div>
                                <span class="balance-card-label">Tier</span>
                                <span class="balance-card-value">{{ dashboardView?.tier_name && dashboardView.tier_name !== '-' ? (dashboardView.tier_name.length > 25 ? dashboardView.tier_name.substring(0, 25) : dashboardView.tier_name) : 'Member' }}</span>
                            </article>
                            <article class="balance-card">
                                <div class="balance-card-icon">
                                    <img src="/img/member-icon/points-logo.svg" alt="Points" />
                                </div>
                                <span class="balance-card-label">Points</span>
                                <span class="balance-card-value">{{ dashboardView?.points || 0 }}</span>
                                <div v-if="isPointValueVisibleToMember" class="balance-card-estimated">
                                    <span class="estimated-value">IDR {{ formatCurrency(dashboardView?.point_balance || 0) }}</span>
                                    <span class="estimated-label">Total Estimated Value</span>
                                </div>
                            </article>
                            <article class="balance-card">
                                <div class="balance-card-icon">
                                    <img src="/img/member-icon/voucher-logo.svg" alt="Vouchers" />
                                </div>
                                <span class="balance-card-label">Vouchers</span>
                                <span class="balance-card-value">{{ dashboardView?.owned_vouchers || 0 }}</span>
                            </article>
                        </div>
                    </section>
                </div>

                <!-- Booking Engine Connected Merchants Section -->
                <member-booking-merchant-card
                    v-if="isBookingModuleEnabled"
                    :merchants="bookingMerchants"
                    :auth-context="bookingAuthContext"
                    :loading="isBookingLoading"
                />

                <div class="bento-grid" :class="{ 'benefit-disabled': !isBenefitModuleEnabled, 'newsletter-disabled': !isNewsletterModuleEnabled, 'promotions-disabled': !isPromotionsModuleEnabled }">

                    <template v-if="isPromotionsModuleEnabled">
                        <section class="content-section" data-bento="offers" v-if="promotionItems?.length">
                            <div class="section-header">
                                <h2 class="section-heading">{{ promotionTitle }}</h2>
                                <span class="section-view-all" @click="handleViewAllPromotions">View all</span>
                            </div>

                            <div v-if="redeemStatus.message" :class="['redeem-feedback', redeemStatus.type === 'error' ? 'redeem-feedback--error' : 'redeem-feedback--success']">
                                {{ redeemStatus.message }}
                            </div>

                            <div class="voucher-grid-desktop">
                                <template v-for="(item, index) in promotionItems" :key="item.id || index">
                                    <member-voucher-content
                                        v-if="item.item_type === 'voucher' && isPointSystemActive"
                                        :voucher="item"
                                        :disabled="!isPointSystemActive"
                                        :is-locked="item.isLocked"
                                        :required-tier-name="item.requiredTierName"
                                        @click="() => handlePromotionItemClick(item)"
                                    />
                                    <member-benefit-content
                                        v-else-if="item.item_type === 'benefit'"
                                        :benefit="item"
                                        :is-locked="item.isLocked"
                                        :required-tier-name="item.requiredTierName"
                                        @click="() => handlePromotionItemClick(item)"
                                    />
                                </template>
                            </div>
                        </section>
                        <section class="content-section" data-bento="offers" v-else>
                            <div class="section-header">
                                <h2 class="section-heading">{{ promotionTitle }}</h2>
                            </div>
                            <p class="empty-state">No promotions available at the moment</p>
                        </section>
                    </template>

                    <section v-if="isNewsletterModuleEnabled" class="content-section" data-bento="newsletter">
                        <member-newsletter-widget
                            :newsletters="newsletters"
                            :loading="loadingNewsletters"
                        />
                    </section>

                    <section class="content-section" data-bento="transactions">
                        <div class="section-header">
                            <h2 class="section-heading">My Recent Transaction</h2>
                            <span class="section-view-all" @click="router.push('/client/transaction-history')">View all</span>
                        </div>
                        <div class="transaction-grid">
                            <my-transaction
                                v-for="(transaction, index) in recentTransactions"
                                :key="transaction.id || index"
                                :transaction="transaction"
                                :vouchers="dashboardView?.vouchers || []"
                                @open-receipt="showModal(transaction, dashboardView)"
                            />
                            <p v-if="!recentTransactions.length" class="empty-state">No recent transactions</p>
                        </div>
                    </section>

                    <template v-if="isBenefitModuleEnabled">
                        <section class="content-section" data-bento="benefits" v-if="benefitsToShow.length">
                            <div class="section-header">
                                <h2 class="section-heading">My Benefits</h2>
                                <span class="section-view-all" @click="redirectToBenefits">View all</span>
                            </div>
                            <div class="benefit-grid">
                                <my-benefit
                                    v-for="(benefit, index) in benefitsToShow"
                                    :key="benefit.id || index"
                                    :benefit="benefit"
                                    @select="showBenefitDetail"
                                />
                                <button
                                    v-if="remainingBenefitsCount > 0"
                                    type="button"
                                    class="benefit-more"
                                    @click="redirectToBenefits"
                                >
                                    +{{ remainingBenefitsCount }} more
                                </button>
                            </div>
                        </section>
                        <section class="content-section" data-bento="benefits" v-else>
                            <div class="section-header">
                                <h2 class="section-heading">My benefits</h2>
                            </div>
                            <p class="empty-state">No benefits available at the moment</p>
                        </section>
                    </template>
                </div>
            </div>
        </div>

        <benefit-detail-modal
            v-if="benefitModalVisible && selectedBenefit"
            :benefit="selectedBenefit"
            @close="closeBenefitModal"
        />

        <voucher-detail-modal
            v-if="voucherModalVisible && selectedVoucher"
            :voucher="selectedVoucher"
            :disabled="!isPointSystemActive"
            @close="closeVoucherModal"
            @redeem="redeemVoucher"
        />

        <voucher-redeem-status-modal
            v-if="redeemStatusModal.visible"
            :status="redeemStatusModal.status"
            @close="handleRedeemStatusClose"
        />

        <transaction-client
            v-if="isModalVisible"
            @parentCloseModal="closeModal"
            :editedItem="editedItem"
        />

        <!-- Gift Info Modal -->
        <div v-if="isGiftModalOpen" class="gift-modal-overlay" @click="closeModal">
            <div class="gift-modal-content" @click.stop>
                <div class="gift-modal-header">
                    <div class="gift-modal-icon">🎁</div>
                    <button @click="closeModal" class="gift-modal-close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="gift-modal-body">
                    <h2 class="gift-modal-title">{{ giftItem.detail_name }}</h2>
                    <p class="gift-modal-subtitle">
                        <span v-if="giftItem.branch_name || giftItem.merchant_name">
                            {{ giftItem.branch_name }} <span v-if="giftItem.branch_name && giftItem.merchant_name"> • </span> {{ giftItem.merchant_name }}
                        </span>
                        <span v-else>{{ giftItem.corporate_name }}</span>
                    </p>
                    <p class="gift-modal-date">Diterima: {{ formatGiftDate(giftItem.created_at) }}</p>

                    <div v-if="giftItem.gift_note" class="gift-modal-note">
                        <p class="gift-note-label">Pesan dari merchant:</p>
                        <p class="gift-note-text">{{ giftItem.gift_note }}</p>
                    </div>
                    <div v-else class="gift-modal-note gift-note-empty">
                        <p class="gift-note-text">Tidak ada pesan dari merchant.</p>
                    </div>
                </div>
            </div>
        </div>

        <o-qr-code-member-modal
            :isVisible="qrCodeModal"
            :corporateLogo="corporateLogoDisplay"
            :memberId="userInfo?.member?.id || memberId || dashboardView?.member_id"
            :memberName="memberName || dashboardView?.member_name"
            :corporateName="corporateName || userInfo?.member?.corporate_name || dashboardView?.corporate_name"
            :tierName="userInfo?.member?.tier_name || tierName || dashboardView?.tier_name"
            :tierIcon="dashboardView?.tier?.icon?.image_url"
            :memberAvatar="memberAvatarDisplay"
            :memberPoints="qrModalPoints"
            :memberVouchers="qrModalVouchers"
            :memberEmail="qrModalEmail"
            :memberPhone="qrModalPhone"
            @close="closeQrCodeModal"
        />

        <o-modal
            v-if="policyModalVisible"
            @close="closePolicyModal"
            :modalClass="'show d-block'"
            :bodyClass="'p-0'"
            :allow-html="false"
            :is-footer-visible="false"
        >
            <template #header>
                <h5 class="modal-title">Policies</h5>
            </template>
            <template #body>
                <div class="policies-container">
                    <div class="policies-tabs">
                        <button
                            type="button"
                            class="tab-button"
                            :class="{ active: activePolicyTab === 'privacy' }"
                            @click="activePolicyTab = 'privacy'"
                        >
                            Privacy Policy
                        </button>
                        <button
                            type="button"
                            class="tab-button"
                            :class="{ active: activePolicyTab === 'terms' }"
                            @click="activePolicyTab = 'terms'"
                        >
                            Terms & Conditions
                        </button>
                    </div>
                    <div class="policies-body">
                        <public-policy-display v-if="activePolicyTab === 'privacy'" type="privacy" />
                        <public-policy-display v-else type="terms" />
                    </div>
                </div>
            </template>
        </o-modal>

        <tier-upgrade-modal
            v-if="tierUpgradeModalVisible && selectedLockedItem"
            :item-name="selectedLockedItem.name"
            :item-type="selectedLockedItem.item_type || 'voucher'"
            :required-tier-name="selectedLockedItem.requiredTierName"
            :required-tier-icon="selectedLockedItem.requiredTier?.icon?.image_url"
            :current-tier-name="dashboardView?.tier_name || 'Bronze'"
            :current-tier-icon="dashboardView?.tier?.icon?.image_url"
            @close="closeTierUpgradeModal"
            @view-tier-benefits="handleViewTierBenefits"
        />

        <all-promotions-modal
            v-if="allPromotionsModalVisible"
            :items="allPromotionItems"
            :title="promotionTitle"
            @close="closeAllPromotionsModal"
            @click-item="handlePromotionItemClick"
        />
    </div>
</template>

<script>
import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import TransactionClient from './MemberTransaction.vue';
import MemberNewsletterWidget from '@/components/member/MemberNewsletterWidget.vue';
import MemberVoucherContent from '@/components/member/MemberVoucherContent.vue';
import MemberBenefitContent from '@/components/member/MemberBenefitContent.vue';
import MyTransaction from '@/components/member/MyTransaction.vue';
import MyBenefit from '@/components/member/MyBenefit.vue';
import BenefitDetailModal from '@/components/member/BenefitDetailModal.vue';
import VoucherDetailModal from '@/components/member/VoucherDetailModal.vue';
import VoucherRedeemStatusModal from '@/components/member/VoucherRedeemStatusModal.vue';
import { OQrCodeMemberModal, OModal } from '@/components/OmnihControl';
import PublicPolicyDisplay from '@/components/PublicPolicyDisplay.vue';
import TierUpgradeModal from '@/components/member/TierUpgradeModal.vue';
import AllPromotionsModal from '@/components/member/AllPromotionsModal.vue';
import { usePointSystem } from '@/composables/pointsystem';
import MemberTierProgress from '@/components/member/MemberTierProgress.vue';
import MemberBookingMerchantCard from '@/components/member/MemberBookingMerchantCard.vue';
import { useModalHistory } from '@/composables/useModalHistory';
export default {
    name: 'MemberDashboardDesktop',
    props: {
        dashboard: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        TransactionClient,
        MemberNewsletterWidget,
        MemberVoucherContent,
        MemberBenefitContent,
        MyTransaction,
        MyBenefit,
        BenefitDetailModal,
        VoucherDetailModal,
        VoucherRedeemStatusModal,
        OQrCodeMemberModal,
        OModal,
        PublicPolicyDisplay,
        TierUpgradeModal,
        AllPromotionsModal,
        MemberTierProgress,
        MemberBookingMerchantCard,
    },
    setup(props) {
        const { isPointSystemActive, isPointValueEnabled, isPointValueVisibleToMember } = usePointSystem();
        const store = useStore();
        const router = useRouter();

        const dashboardData = ref(props.dashboard ? { ...props.dashboard } : {});
        const benefits = ref([]);
        const displayedBenefitsLimit = 10;
        const selectedBenefit = ref(null);
        const benefitModalVisible = ref(false);

        const selectedVoucher = ref(null);

        // Booking Engine Connected Merchants state
        const isBookingModuleEnabled = computed(() => {
            const flagObj = store.state.FeatureFlag?.bookingModule;
            if (flagObj) {
                return flagObj.flag === 'ACTIVE';
            }
            if (store.state.FeatureFlag?.loading) {
                return false;
            }
            return true;
        });
        const bookingMerchants = computed(() => store.getters['ClientBooking/connectedMerchants'] || []);
        const bookingAuthContext = computed(() => store.getters['ClientBooking/authContext'] || null);
        const isBookingLoading = computed(() => {
            return isBookingModuleEnabled.value && Boolean(store.getters['ClientBooking/isLoading']);
        });

        const fetchBookingMerchants = async () => {
            if (!isBookingModuleEnabled.value) return;
            try {
                await store.dispatch('ClientBooking/fetchConnectedMerchants');
            } catch (error) {
                console.error('Error fetching connected booking merchants:', error);
            }
        };
        const voucherModalVisible = ref(false);
        const redeemStatus = ref({ type: '', message: '' });
        const redeemStatusModal = ref({ visible: false, status: 'loading' });

        const isModalVisible = ref(false);
        const isGiftModalOpen = ref(false);
        const giftItem = ref({});
        const editedItem = ref({
            id: '',
            name: '',
            transaction_value: '',
            title: '',
        });

        const qrCodeModal = ref(false);
        const policyModalVisible = ref(false);
        const activePolicyTab = ref('privacy');
        
        const newsletters = ref([]);
        const loadingNewsletters = ref(false);

        // Check if newsletter module is enabled via feature flag
        const isNewsletterModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.newsletterModule?.flag || 'ACTIVE') === 'ACTIVE';
        });

        // State untuk tier upgrade modal
        const tierUpgradeModalVisible = ref(false);
        const selectedLockedItem = ref(null);
        
        // State for All Promotions Modal
        const allPromotionsModalVisible = ref(false);

        const memberId = ref('');
        const memberName = ref('');
        const corporateName = ref('');
        const corporateLogo = ref('');
        const tierName = ref('');
        const memberAvatar = ref('');

        // Tier progress data
        const allTiers = ref([]);
        const totalTransaction = ref(0);
        const currentTierName = ref('');
        const currentTierData = ref(null);

        // Modal History (Back Button Handler)
        const { pushModalHistory: pushTransactionModal, popModalHistory: popTransactionModal } = useModalHistory({
            isVisible: isModalVisible,
            onClose: () => { isModalVisible.value = false },
            stateKey: 'desktop-transaction-modal'
        })

        const { pushModalHistory: pushBenefitModal, popModalHistory: popBenefitModal } = useModalHistory({
            isVisible: benefitModalVisible,
            onClose: () => {
                benefitModalVisible.value = false
                selectedBenefit.value = null
            },
            stateKey: 'desktop-benefit-modal'
        })

        const { pushModalHistory: pushVoucherModal, popModalHistory: popVoucherModal } = useModalHistory({
            isVisible: voucherModalVisible,
            onClose: () => {
                voucherModalVisible.value = false
                selectedVoucher.value = null
            },
            stateKey: 'desktop-voucher-modal'
        })

        const { pushModalHistory: pushTierUpgradeModal, popModalHistory: popTierUpgradeModal } = useModalHistory({
            isVisible: tierUpgradeModalVisible,
            onClose: () => {
                tierUpgradeModalVisible.value = false
                selectedLockedItem.value = null
            },
            stateKey: 'desktop-tier-upgrade-modal'
        })

        const { pushModalHistory: pushAllPromotionsModal, popModalHistory: popAllPromotionsModal } = useModalHistory({
            isVisible: allPromotionsModalVisible,
            onClose: () => { allPromotionsModalVisible.value = false },
            stateKey: 'desktop-all-promotions-modal'
        })

        const { popModalHistory: popQrCodeModal } = useModalHistory({
            isVisible: qrCodeModal,
            onClose: () => { qrCodeModal.value = false },
            stateKey: 'desktop-qrcode-modal'
        })

        const { pushModalHistory: pushGiftModal, popModalHistory: popGiftModal } = useModalHistory({
            isVisible: isGiftModalOpen,
            onClose: () => { isGiftModalOpen.value = false },
            stateKey: 'dashboard-gift-modal'
        })

        const { popModalHistory: popPolicyModal } = useModalHistory({
            isVisible: policyModalVisible,
            onClose: () => {
                policyModalVisible.value = false
                activePolicyTab.value = 'privacy'
            },
            stateKey: 'desktop-policy-modal'
        })

        onBeforeUnmount(() => {
            document.body.style.overflow = ''
        })

        watch([isModalVisible, isGiftModalOpen, benefitModalVisible, voucherModalVisible, tierUpgradeModalVisible, allPromotionsModalVisible, qrCodeModal, policyModalVisible], (vals) => {
            const anyOpen = vals.some(Boolean)
            document.body.style.overflow = anyOpen ? 'hidden' : ''
        })


        // userInfo dari Auth/auth_check yang berisi data lengkap dari /api/auth/self
        const userInfo = computed(() => store.state.Auth.userInfo);
        
        // currUser untuk backward compatibility
        const userStore = computed(() => store.state.Auth.currUser);

        const hasDashboardData = computed(() => Object.keys(dashboardData.value || {}).length > 0);
        const dashboardView = computed(() => (hasDashboardData.value ? dashboardData.value : props.dashboard || {}));

        const benefitsToShow = computed(() => {
            if (!Array.isArray(benefits.value)) return [];
            return benefits.value.slice(0, displayedBenefitsLimit);
        });

        const isBenefitModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.benefitModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const isPointControlEnabled = computed(() => {
            const flag = store.state.FeatureFlag?.pointControl?.flag;
            return flag === 'CENTRALIZE' || flag === 'DECENTRALIZE';
        })

        const isPromotionsModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.promotionsModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const remainingBenefitsCount = computed(() => {
            if (!Array.isArray(benefits.value)) return 0;
            const remaining = benefits.value.length - displayedBenefitsLimit;
            return remaining > 0 ? remaining : 0;
        });

        /**
         * Computed property untuk title promotion section
         * Menggunakan title dari promotion_section API atau default
         */
        const promotionTitle = computed(() => {
            return dashboardView.value?.promotion_section?.title || 'Limited offers';
        });

        /**
         * Computed property untuk items promotion section
         * Termasuk locked items dengan tier comparison logic
         */
        const allPromotionItems = computed(() => {
            const promotionSection = dashboardView.value?.promotion_section;
            if (!promotionSection?.items || !Array.isArray(promotionSection.items)) return [];
            
            const now = new Date();
             // displayLimit logic moved to promotionItems
            const currentTier = dashboardView.value?.tier;
            const currentTierMinTransaction = parseFloat(currentTier?.transaction_value_min || 0);
            
            // Filter items based on expiry and tier visibility (higher tiers don't see lower tier vouchers)
            const filteredItems = promotionSection.items
                .filter(item => {
                    // Check expiry
                    if (item.expiration) {
                        const expirationDate = new Date(item.expiration);
                        if (expirationDate <= now) return false;
                    }

                    // Check tier visibility (Deals match: high tier hides low tier vouchers)
                    if (item.member_tier_id || item.tier_id) {
                        const itemTier = item.tier;
                        if (itemTier) {
                            const itemTierMinTransaction = parseFloat(itemTier.transaction_value_min || 0);
                            // If member tier is higher than voucher tier, hide it
                            if (currentTierMinTransaction > itemTierMinTransaction) return false;
                        }
                    }
                    
                    return true;
                })
                .map(item => {
                    // Determine if item is locked based on tier requirement
                    // Cari tier data dari item atau fallback ke member_tier_id
                    let requiredTier = null;
                    let isLocked = false;
                    
                    if (item.member_tier_id || item.tier_id) {
                        // Jika ada member_tier_id atau tier_id, cari tier data dari item
                        requiredTier = item.tier || { 
                            name: 'Higher Tier', 
                            transaction_value_min: '999999999' 
                        };
                        
                        const itemTierMinTransaction = parseFloat(requiredTier.transaction_value_min || '999999999');
                        isLocked = itemTierMinTransaction > currentTierMinTransaction;
                    }
                    
                    return {
                        ...item,
                        isLocked,
                        requiredTierName: isLocked ? requiredTier?.name || 'Higher Tier' : null,
                        requiredTier
                    };
                })
            // Sort ONLY by sort_order (ascending) - ignore locked status
                .sort((a, b) => {
                    const sortOrderA = parseInt(a.sort_order) || 999;
                    const sortOrderB = parseInt(b.sort_order) || 999;
                    return sortOrderA - sortOrderB;
                });

            let finalItems = filteredItems;

            if (!isBenefitModuleEnabled.value) {
                finalItems = finalItems.filter(item => item.item_type !== 'benefit');
            }
            if (!isPointControlEnabled.value) {
                finalItems = finalItems.filter(item => item.item_type !== 'voucher');
            }
            
            return finalItems;
        });

        const promotionItems = computed(() => {
             const displayLimit = dashboardView.value?.promotion_section?.display_limit || 5;
             return allPromotionItems.value.slice(0, displayLimit);
        });

        const recentTransactions = computed(() => {
            const currentDashboard = dashboardView.value;
            if (!currentDashboard || !currentDashboard.point_histories) {
                return [];
            }

            const limit = isBenefitModuleEnabled.value ? 5 : 6;

            return currentDashboard.point_histories
                .slice()
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, limit);
        });

        const corporateLogoDisplay = computed(() => corporateLogo.value || '/img/member-icon/tier-logo.svg');
        const memberAvatarDisplay = computed(() => memberAvatar.value || '');
        
        // Computed untuk QR modal props - menggunakan userInfo dari /api/auth/self
        const qrModalPoints = computed(() => {
            return userInfo.value?.member?.points ?? dashboardData.value?.points ?? props.dashboard?.points ?? 0;
        });
        
        // Prioritas: dashboardData (sudah ter-refresh setelah redeem/cancel) > userInfo > props
        const qrModalVouchers = computed(() => {
            return dashboardData.value?.owned_vouchers ?? userInfo.value?.member?.filtered_vouchers?.length ?? props.dashboard?.owned_vouchers ?? 0;
        });
        
        const qrModalEmail = computed(() => {
            return userInfo.value?.email || dashboardData.value?.member_email || props.dashboard?.member_email || '';
        });
        
        const qrModalPhone = computed(() => {
            return userInfo.value?.user_profile?.phone || dashboardData.value?.member_profile?.phone || props.dashboard?.member_profile?.phone || '';
        });
        const userInitials = computed(() => {
            const name = memberName.value || dashboardView.value?.member_name || '';
            if (!name) return '?';
            return name
                .split(' ')
                .filter(Boolean)
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
        });


        const loadMemberProfile = async (member) => {
            memberId.value = member?.member?.id || '';
            memberName.value = `${member?.title || member?.member?.title || ''} ${member?.first_name || member?.member?.first_name || ''} ${member?.last_name || member?.member?.last_name || ''}`.trim();
            corporateName.value = member?.member?.corporate_name || '';
            corporateLogo.value = member?.member?.corporate_logo || '';
            tierName.value = member?.member?.tier_name || '';
            
            // Fetch avatar dari API jika tidak ada di member data (fix untuk native)
            const avatarFromMember = member?.member?.member_avatar || '';
            if (avatarFromMember) {
                memberAvatar.value = avatarFromMember;
            } else {
                // Fallback: fetch dari API User/getAvatar
                const userId = member?.id || localStorage.getItem('id');
                if (userId) {
                    try {
                        const resp = await store.dispatch('User/getAvatar', userId);
                        memberAvatar.value = resp.data.data || '';
                    } catch (error) {
                        console.error('Error fetching avatar:', error);
                        memberAvatar.value = '';
                    }
                }
            }
        };

        watch(
            userStore,
            (newVal) => {
                if (newVal?.member) {
                    loadMemberProfile(newVal);
                }
            },
            { immediate: true, deep: true }
        );

        watch(
            () => props.dashboard,
            (newVal) => {
                if (newVal && !hasDashboardData.value) {
                    dashboardData.value = { ...newVal };
                }
            }
        );

        watch(isBookingModuleEnabled, (enabled) => {
            if (enabled && bookingMerchants.value.length === 0) {
                fetchBookingMerchants();
            }
        });

        onMounted(() => {
            loadAllData();
        });

        const loadAllData = async () => {
            try {
                await Promise.all([
                    fetchDashboard(),
                    loadBenefitsData(),
                    fetchNewsletters(),
                    fetchBookingMerchants()
                ]);
                // Load tier data only when point system is NONACTIVE
                if (!isPointSystemActive.value) {
                    await loadTierData();
                }
            } catch (error) {
                console.error('Error loading all data:', error);
            }
        };

        const fetchDashboard = async () => {
            try {
                const response = await store.dispatch('ClientDashboard/read');
                const latestDashboard = response?.data?.data;
                if (latestDashboard) {
                    dashboardData.value = { ...latestDashboard };
                }
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            }
        };

        const loadBenefitsData = async () => {
            try {
                const benefitResponse = await store.dispatch('ClientBenefit/read');
                const data = benefitResponse.data.data;

                if (data.benefit && typeof data.benefit === 'object') {
                    benefits.value = Object.values(data.benefit);
                }
            } catch (error) {
                console.error('Error loading benefits data:', error);
            }
        };

        const fetchNewsletters = async () => {
            loadingNewsletters.value = true;
            try {
                const response = await store.dispatch('ClientNewsletter/read', { limit: 5 });
                const data = response?.data?.data || response?.data;
                if (data?.data && Array.isArray(data.data)) {
                    newsletters.value = data.data;
                } else if (Array.isArray(data)) {
                    newsletters.value = data;
                }
            } catch (error) {
                console.error('Error fetching newsletters:', error);
            } finally {
                loadingNewsletters.value = false;
            }
        };

        function formatCurrency(value) {
            if (!value && value !== 0) return '0';
            return new Intl.NumberFormat('id-ID').format(value);
        }

        function getTierState(tierIndex, currentIndex, isLastTier) {
            if (tierIndex < currentIndex) return 'passed';
            if (tierIndex === currentIndex) {
                if (isLastTier && tierIndex === allTiers.value.length - 1) return 'max';
                return 'active';
            }
            return 'locked';
        }

        function calculateTierProgress(tierIndex, currentIndex, tierData, nextTierData) {
            if (tierIndex < currentIndex) return 100;
            if (tierIndex > currentIndex) return 0;
            
            const tierMin = parseFloat(tierData?.transaction_value_min) || 0;
            const nextMin = parseFloat(nextTierData?.transaction_value_min) || tierMin;
            
            if (!nextTierData || nextMin <= tierMin) return 100;
            
            const range = nextMin - tierMin;
            const currentProgress = totalTransaction.value - tierMin;
            
            if (currentProgress <= 0) return 0;
            if (currentProgress >= range) return 100;
            
            return (currentProgress / range) * 100;
        }

        async function loadTierData() {
            try {
                const response = await store.dispatch('ClientBenefit/read');
                const data = response.data.data;
                
                allTiers.value = data.tier || [];
                totalTransaction.value = data.total_transaction || 0;
                currentTierName.value = data.tier_name || '';
                
                if (allTiers.value.length > 0) {
                    const sortedTiers = [...allTiers.value].sort((a, b) => parseFloat(a.transaction_value_min) - parseFloat(b.transaction_value_min));
                    const currentIndex = sortedTiers.findIndex(t => t.name === currentTierName.value);
                    
                    let currentTier, nextTier, state, progressPercentage;

                    if (currentIndex === -1) {
                        currentTier = {
                            name: currentTierName.value || 'Member',
                            transaction_value_min: 0,
                            icon: { image_url: 'fas fa-medal' }
                        };
                        nextTier = sortedTiers[0] || null;
                        state = 'active';

                        const nextMin = parseFloat(nextTier?.transaction_value_min) || 0;
                        if (nextMin > 0 && totalTransaction.value > 0) {
                            progressPercentage = (totalTransaction.value / nextMin) * 100;
                            if (progressPercentage > 100) progressPercentage = 100;
                        } else {
                            progressPercentage = 0;
                        }
                    } else {
                        const isAtMaxTier = currentIndex === sortedTiers.length - 1;
                        currentTier = sortedTiers[currentIndex];
                        nextTier = sortedTiers[currentIndex + 1] || null;
                        state = getTierState(currentIndex, currentIndex, isAtMaxTier);
                        progressPercentage = calculateTierProgress(currentIndex, currentIndex, currentTier, nextTier);
                    }
                    
                    currentTierData.value = {
                        state,
                        progressPercentage,
                        currentAmount: formatCurrency(totalTransaction.value),
                        currentTierData: {
                            name: currentTier.name,
                            amount: currentIndex === -1 ? `IDR. 0` : `IDR. ${formatCurrency(currentTier.transaction_value_min)}`,
                            icon: currentTier.icon?.image_url || (currentIndex === -1 ? 'fas fa-medal' : '/img/member-icon/tier-gold-example.svg')
                        },
                        nextTierData: nextTier ? {
                            name: nextTier.name,
                            amount: `IDR. ${formatCurrency(nextTier.transaction_value_min)}`,
                            icon: nextTier.icon?.image_url || '/img/member-icon/tier-platinum-example.svg'
                        } : {
                            name: 'MAX',
                            amount: 'Maximum Tier',
                            icon: currentTier.icon?.image_url || '/img/member-icon/tier-platinum-example.svg'
                        }
                    };
                }
            } catch (error) {
                console.error('Error loading tier data:', error);
            }
        }

        const showBenefitDetail = (benefit) => {
            selectedBenefit.value = benefit;
            benefitModalVisible.value = true;
            pushBenefitModal();
        };

        const closeBenefitModal = () => {
            benefitModalVisible.value = false;
            selectedBenefit.value = null;
            popBenefitModal();
        };

        const redirectToBenefits = () => {
            router.push('/client/benefit');
        };

        const showVoucherDetail = (voucher) => {
            selectedVoucher.value = {
                ...voucher,
                id: voucher?.id ?? voucher?.voucher_id ?? voucher?.voucherId,
            };
            voucherModalVisible.value = true;
            pushVoucherModal();
        };

        const closeVoucherModal = () => {
            voucherModalVisible.value = false;
            selectedVoucher.value = null;
            popVoucherModal();
        };

        /**
         * Handler untuk click pada promotion item
         * Menentukan behavior berdasarkan item_type dan locked status
         * @param {Object} item - Data promotion item
         */
        const handlePromotionItemClick = (item) => {
            // If item is locked, show tier upgrade modal
            if (item.isLocked) {
                selectedLockedItem.value = item;
                tierUpgradeModalVisible.value = true;
                pushTierUpgradeModal();
                return;
            }
            
            const itemType = item?.item_type || 'voucher';
            
            if (itemType === 'benefit') {
                showBenefitDetail(item);
            } else {
                showVoucherDetail(item);
            }
        };

        /**
         * Menutup tier upgrade modal
         */
        const closeTierUpgradeModal = () => {
            tierUpgradeModalVisible.value = false;
            selectedLockedItem.value = null;
            popTierUpgradeModal();
        };

        /**
         * Handler untuk view tier benefits dari modal
         */
        const handleViewTierBenefits = () => {
            closeTierUpgradeModal(); // Pop history state first
            
            setTimeout(() => {
                router.push('/client/benefit'); // Navigate safely internal SPA mode
            }, 300);
        };

        const redeemVoucher = (voucher) => {
            closeVoucherModal();
            redeemDeal(voucher);
        };

        const redeemDeal = (voucher) => {
            const voucherId = voucher?.id ?? voucher?.voucher_id ?? voucher?.voucherId;

            if (!voucherId) {
                redeemStatus.value = {
                    type: 'error',
                    message: 'Failed redeeming voucher. Missing voucher ID.',
                };
                return;
            }

            const payload = {
                merchant_id: voucher.merchant_id,
                memberDeal: true,
                quantity: 1,
                voucher_id: voucherId,
                expiration: voucher.expiration,
                cancelable: voucher.cancelable,
            };

            if (voucher.cancelable === 1 && voucher.cancellation_cutoff) {
                const expirationDate = new Date(voucher.expiration);
                const cutoffMs = voucher.cancellation_cutoff * 1000;
                const cancelableBeforeDate = new Date(expirationDate.getTime() - cutoffMs);

                const year = cancelableBeforeDate.getFullYear();
                const month = String(cancelableBeforeDate.getMonth() + 1).padStart(2, '0');
                const day = String(cancelableBeforeDate.getDate()).padStart(2, '0');
                const hours = String(cancelableBeforeDate.getHours()).padStart(2, '0');
                const minutes = String(cancelableBeforeDate.getMinutes()).padStart(2, '0');
                const seconds = String(cancelableBeforeDate.getSeconds()).padStart(2, '0');

                payload.cancelable_before = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }

            redeemStatusModal.value = { visible: true, status: 'loading' };

            store
                .dispatch('ClientVoucher/redeem', { data: payload })
                .then(async () => {
                    await loadAllData();

                    redeemStatus.value = {
                        type: 'success',
                        message: 'Voucher redeemed successfully.',
                    };
                    setTimeout(() => {
                        redeemStatus.value = { type: '', message: '' };
                    }, 3000);
                    redeemStatusModal.value = { visible: true, status: 'success' };
                })
                .catch((error) => {
                    if (error.response?.data?.message === 'VOUCHER_REDEEM.CREATE_SINGLE_DATA.MODEL.EXCEPTION' &&
                        error.response?.data?.error === 'Member not have enough point to redeem this voucher') {
                        redeemStatus.value = { type: '', message: '' };
                        redeemStatusModal.value = { visible: true, status: 'error' };
                    } else {
                        const errorMessage = getRedeemErrorMessage(error);
                        redeemStatus.value = {
                            type: 'error',
                            message: errorMessage,
                        };
                        redeemStatusModal.value = { visible: true, status: 'loading' };
                        setTimeout(() => {
                            redeemStatusModal.value.visible = false;
                        }, 1500);
                    }
                });
        };

        const handleViewAllPromotions = () => {
             // Always show modal as requested by user
             allPromotionsModalVisible.value = true;
             pushAllPromotionsModal();
        };

        /**
         * Convert technical error messages to user-friendly messages
         * @param {Object} error - Error object from API
         * @returns {string} User-friendly error message
         */
        const getRedeemErrorMessage = (error) => {
            const apiMessage = error?.response?.data?.message || '';
            const apiError = error?.response?.data?.error || '';
            
            const errorMap = {
                'VOUCHER_POINT_VALIDATE.EXCEPTION': 'You don\'t have enough points to redeem this voucher.',
                'VOUCHER_EXPIRE_VALIDATE.EXCEPTION': 'This voucher has expired.',
                'VOUCHER_VALIDATE.EXCEPTION': 'This voucher cannot be redeemed at this time.'
            };

            if (errorMap[apiMessage]) {
                return errorMap[apiMessage];
            }

            if (apiError.includes('not have enough point')) {
                return 'You don\'t have enough points to redeem this voucher.';
            }

            if (apiError.includes('vouchers left in stock') || apiError.includes('Only')) {
                return apiError;
            }

            if (apiError.includes('expired')) {
                return 'This voucher has expired and can no longer be redeemed.';
            }

            return 'Failed to redeem voucher. Please try again later.';
        };

        const handleRedeemStatusClose = () => {
            redeemStatusModal.value = { visible: false, status: 'loading' };
        };

        const showModal = (data, member) => {
            // Gift vouchers open the gift info modal instead
            if (data.type === 'gift') {
                giftItem.value = data;
                isGiftModalOpen.value = true;
                pushGiftModal();
                return;
            }

            const profile = member?.member_profile || {};
            editedItem.value = {
                id: data.id,
                member_name: member.member_name,
                member_email: member.member_email,
                phone: profile.phone,
                state: profile.state,
                address: profile.address,
                country: profile.country,
                merchant_id: data.merchant_id,
                corporate_name: data.corporate_name,
                branch_name: data.branch_name,
                merchant_name: data.merchant_name,
                type: data.type,
                created_at: data.created_at,
                qty: data.qty,
                detail_name: data.detail_name || 'Deduction Point',
                detail_code: data.detail_code,
                transaction_code: data.transaction_code,
                transaction_value: data.subtotal,
                voucher_discount: data.voucher_discount,
                amount_after_voucher: data.amount_after_voucher,
                voucher_detail: data.voucher_detail,
                point:
                    data.point ??
                    (data.point_deducted !== undefined && data.point_deducted !== null
                        ? `-${Math.abs(data.point_deducted)}`
                        : null),
                business_logo: data.merchant?.business_profile?.logo?.image_url ||
                    data.merchant?.businessProfile?.logo?.image_url ||
                    data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    member.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    member.branch_images?.[0]?.businessProfile?.logo?.image_url,
                fallback_logo: data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    member.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    member.branch_images?.[0]?.businessProfile?.logo?.image_url,
                reference_code: data.refference_code,
                is_pending: data.is_pending,
                tax_charge: data.tax_charge,
                service_charge: data.service_charge,
                tax_value: data.tax_value,
                service_value: data.service_value,
                before_tax_service: data.before_tax_service,
                after_tax_service: data.after_tax_service,
                is_nett_price: data.is_nett_price,
                title: 'My Transaction Receipt',
            };
            isModalVisible.value = true;
            pushTransactionModal();
        };

        const closeModal = () => {
            if (isModalVisible.value) {
                isModalVisible.value = false;
                popTransactionModal();
            }
            if (isGiftModalOpen.value) {
                isGiftModalOpen.value = false;
                popGiftModal();
            }
        };

        function formatGiftDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const day = String(date.getDate()).padStart(2, '0');
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const period = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${day} ${month} ${year} • ${String(hours).padStart(2, '0')}.${minutes} ${period}`;
        }

        // Sidebar logic moved to NavbarMemberDesktop component

        const closePolicyModal = () => {
            policyModalVisible.value = false;
            activePolicyTab.value = 'privacy';
            popPolicyModal();
        };

        const closeQrCodeModal = () => {
            qrCodeModal.value = false;
            popQrCodeModal();
        };

        const closeAllPromotionsModal = () => {
            allPromotionsModalVisible.value = false;
            popAllPromotionsModal();
        };

        return {
            isBookingModuleEnabled,
            bookingMerchants,
            bookingAuthContext,
            isBookingLoading,
            router,
            dashboardView,
            benefits,
            benefitsToShow,
            remainingBenefitsCount,
            recentTransactions,
            selectedBenefit,
            benefitModalVisible,
            showBenefitDetail,
            closeBenefitModal,
            redirectToBenefits,
            selectedVoucher,
            voucherModalVisible,
            showVoucherDetail,
            closeVoucherModal,
            redeemVoucher,
            redeemStatus,
            redeemStatusModal,
            handleRedeemStatusClose,
            isModalVisible,
            editedItem,
            isGiftModalOpen,
            giftItem,
            formatGiftDate,
            showModal,
            closeModal,
            qrCodeModal,
            memberId,
            memberName,
            corporateName,
            corporateLogoDisplay,
            tierName,
            memberAvatarDisplay,
            userInitials,
            policyModalVisible,
            closePolicyModal,
            activePolicyTab,
            corporateLogo,
            qrModalPoints,
            qrModalVouchers,
            qrModalEmail,
            qrModalPhone,
            userStore,
            userInfo,
            promotionTitle,
            promotionItems,
            handlePromotionItemClick,
            tierUpgradeModalVisible,
            selectedLockedItem,
            closeTierUpgradeModal,
            handleViewTierBenefits,
            handleViewAllPromotions,
            allPromotionsModalVisible,
            closeAllPromotionsModal,
            allPromotionItems,
            isPointSystemActive,
            isPointValueEnabled,
            isPointValueVisibleToMember,
            formatCurrency,
            currentTierData,
            newsletters,
            loadingNewsletters,
            isNewsletterModuleEnabled,
            isBenefitModuleEnabled,
            isPromotionsModuleEnabled,
            isPointControlEnabled,
            closeQrCodeModal
        };
    },
};
</script>

<style scoped>
.dashboard-desktop {
    display: flex;
    grid-template-columns: 280px 1fr; /* Default state */
    min-height: 100vh;
    background-color: #f5f6fa;
    transition: grid-template-columns 0.3s ease;
}

.dashboard-desktop.sidebar-collapsed {
    grid-template-columns: 92px 1fr; /* Collapsed state */
}

.dashboard-main {
    background-color: #f5f6fa;
    overflow-y: auto;
    flex: 1;
    min-width: 0;
    width: 100%;
}

.main-container {
    padding: 24px 32px;
    max-width: 1440px;
    margin: 0 auto;
}



/* Bento Grid Layout */
.bento-grid {
    display: grid;
    grid-template-columns: 1fr 1.5fr; /* Balance smaller than offers */
    grid-template-rows: auto auto; /* Two rows */
    grid-template-areas:
        'offers offers'
        'newsletter newsletter'
        'transactions benefits';
    gap: 24px;
    margin-top: 24px;
}

.bento-grid.benefit-disabled {
    grid-template-columns: 1fr;
    grid-template-areas:
        'offers'
        'newsletter'
        'transactions';
}

.bento-grid.newsletter-disabled {
    grid-template-areas:
        'offers offers'
        'transactions benefits';
}

.bento-grid.newsletter-disabled.benefit-disabled {
    grid-template-columns: 1fr;
    grid-template-areas:
        'offers'
        'transactions';
}

.bento-grid.promotions-disabled {
    grid-template-areas:
        'newsletter newsletter'
        'transactions benefits';
}

.bento-grid.promotions-disabled.benefit-disabled {
    grid-template-columns: 1fr;
    grid-template-areas:
        'newsletter'
        'transactions';
}

.bento-grid.promotions-disabled.newsletter-disabled {
    grid-template-areas:
        'transactions benefits';
}

.bento-grid.promotions-disabled.newsletter-disabled.benefit-disabled {
    grid-template-columns: 1fr;
    grid-template-areas:
        'transactions';
}

[data-bento="offers"] {
    grid-area: offers;
    min-width: 0;
    overflow: hidden;
}

[data-bento="transactions"] {
    grid-area: transactions;
}

[data-bento="benefits"] {
    grid-area: benefits;
}

[data-bento="newsletter"] {
    grid-area: newsletter;
}

.content-section {
    background-color: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    height: 100%; /* Ensure sections fill the grid cell height */
}

.hero-background {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    align-items: stretch;
    gap: 24px;
    background-color: var(--primary-bg, #F93E3E);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 12px 24px var(--secondary-color, rgba(249, 62, 62, 0.16));
}

.dashboard-hero {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    color: #FFFFFF;
}

.hero-text {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
}

.hero-subtitle {
    font-size: 14px;
    margin: 0;
    font-weight: 400;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.hero-title {
    font-size: 28px;
    font-weight: 600;
    margin: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.3;
}

.hero-qr {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FFFFFF;
    color: var(--primary-color, #F93E3E);
    border-radius: 12px;
    border: none;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease;
}

.hero-qr img {
    width: 18px;
    height: 18px;
}

.hero-qr:hover {
    background: var(--secondary-color, #FFE6E6);
}

.balance-section {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
}

.section-heading {
    font-size: 18px;
    font-weight: 520;
    margin: 0;
    color: #1B1E28;
}

.balance-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.balance-grid--single {
    grid-template-columns: 1fr;
    justify-items: stretch;
}

.tier-progress-wrapper-desktop {
    width: 100%;
    padding: 16px;
    background: transparent;
}

.tier-progress-title-desktop {
    font-size: 16px;
    font-weight: 600;
    color: #1B1E28;
    margin-bottom: 16px;
    text-align: center;
}

.tier-loading-desktop {
    text-align: center;
    color: #717784;
    padding: 32px;
    font-size: 14px;
}

.balance-card-icon--large img {
    width: 72px !important;
    height: 72px !important;
}

.balance-card-value--large {
    font-size: 24px !important;
    font-weight: 700 !important;
}

.balance-card {
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    text-align: center;
    min-height: 160px;
}

.balance-card-icon {
    width: fit-content;
    height: fit-content;
    padding: 12px;
    border-radius: 16px;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
}

.balance-card-icon img {
    width: 32px;
    height: 32px;
    object-fit: contain;
}

.balance-card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.balance-card-label {
    font-size: 12px;
    color: #717784;
    letter-spacing: 0.08em;
    min-height: 16px;
}

.balance-card-value {
    font-size: 20px;
    font-weight: 600;
    color: #1B1E28;
    min-height: 30px;
    display: flex;
    align-items: center;
}

.balance-card-subtitle {
    font-size: 12px;
    color: #717784;
    margin-top: 4px;
    min-height: 18px;
}

.balance-card-estimated {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0px;
    margin-top: 4px;
    padding: 6px 8px;
    background: var(--secondary-color, rgba(249, 62, 62, 0.06));
    border-radius: 6px;
}

.balance-card-estimated .estimated-value {
    font-size: 11px;
    font-weight: 600;
    color: var(--primary-color, #F93E3E);
    letter-spacing: -0.01em;
    line-height: 1.5;
}

.balance-card-estimated .estimated-label {
    font-size: 8px;
    font-weight: 500;
    color: #717784;
    letter-spacing: 0.02em;
    line-height: 1.5;
}

.content-section {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.section-view-all {
    font-size: 14px;
    color: #717784;
    cursor: pointer;
}

.section-view-all:hover {
    color: var(--primary-color, #F93E3E);
}

.voucher-grid-desktop {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(209, 213, 219, 0.8) transparent;
}

.voucher-grid-desktop::-webkit-scrollbar {
    height: 3px;
}

.voucher-grid-desktop::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 99px;
}

.voucher-grid-desktop::-webkit-scrollbar-thumb {
    background: rgba(209, 213, 219, 0.8);
    border-radius: 99px;
    transition: background 0.2s ease;
}

.voucher-grid-desktop::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.9);
}

.voucher-grid-desktop ::v-deep(.voucher-content) {
    width: 280px;
    min-width: 280px;
    flex: 0 0 auto;
}

.transaction-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 16px;
    align-items: stretch;
}

.benefit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
    align-items: stretch;
}

.benefit-more {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px dashed #E1E4EA;
    color: var(--primary-color, #F93E3E);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    grid-column: 1 / -1;
    width: 100%;
}

.benefit-more:hover {
    background: var(--secondary-color, #FFF5F5);
}

.medal-icon-desktop {
    color: var(--primary-icon, #F93E3E);
}

.redeem-feedback {
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
}

.redeem-feedback--success {
    background: rgba(74, 222, 128, 0.16);
    color: #15803D;
}

.redeem-feedback--error {
    background: rgba(248, 113, 113, 0.16);
    color: #B91C1C;
}

.empty-state {
    color: #717784;
    text-align: center;
    padding: 20px 12px;
    margin: 0;
}

.policies-container {
    height: 450px;
    display: flex;
    flex-direction: column;
}

.policies-tabs {
    display: flex;
    gap: 8px;
    padding: 16px 20px 0;
}

.tab-button {
    flex: 1;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: white;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.tab-button.active {
    background: var(--secondary-color, rgba(249, 62, 62, 0.12));
    color: var(--primary-color, #fc6666);
    border-color: var(--primary-border, rgba(249, 62, 62, 0.4));
}

.policies-body {
    padding: 20px;
    flex: 1;
    overflow-y: auto;
}

@media (max-width: 1280px) {
    .dashboard-desktop {
        grid-template-columns: 240px 1fr;
    }
}

@media (max-width: 1024px) {
    .dashboard-desktop {
        grid-template-columns: 220px 1fr;
    }

    /* .dashboard-main {
        padding: 24px;
    } */

    .dashboard-hero {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 900px) {
    .dashboard-desktop {
        grid-template-columns: 1fr;
    }

    .dashboard-sidebar {
        position: relative;
        height: auto;
        border-radius: 0 0 24px 24px;
        flex-direction: row;
        flex-wrap: wrap;
    }

    .dashboard-sidebar.is-collapsed {
        width: 100%;
    }

    .sidebar-toggle {
        position: static;
        margin-left: auto;
    }

    .sidebar-menu {
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
    }

    .sidebar-menu-item {
        flex: 1 0 120px;
        justify-content: center;
    }

    /* .dashboard-main {
        padding: 24px 16px 48px;
    } */
}

.gift-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    backdrop-filter: blur(2px);
    animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}

.gift-modal-content {
    background: #fff;
    border-radius: 20px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    animation: slideUp 0.25s ease;
}

@keyframes slideUp {
    from { transform: translateY(32px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
}

.gift-modal-header {
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 16px;
}

.gift-modal-icon {
    font-size: 40px;
    line-height: 1;
}

.gift-modal-close {
    background: rgba(255, 255, 255, 0.7);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #065F46;
    transition: background 0.2s;
}

.gift-modal-close:hover {
    background: rgba(255, 255, 255, 0.95);
}

.gift-modal-body {
    padding: 20px 24px 28px;
}

.gift-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
    line-height: 1.3;
}

.gift-modal-subtitle {
    font-size: 13px;
    color: #6B7280;
    margin: 0 0 4px;
}

.gift-modal-date {
    font-size: 12px;
    color: #9CA3AF;
    margin: 0 0 20px;
}

.gift-modal-note {
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 12px;
    padding: 14px 16px;
}

.gift-modal-note.gift-note-empty {
    background: #F9FAFB;
    border-color: #E5E7EB;
}

.gift-note-label {
    font-size: 11px;
    font-weight: 600;
    color: #065F46;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
}

.gift-note-text {
    font-size: 14px;
    color: #374151;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
}

.gift-note-empty .gift-note-text {
    color: #9CA3AF;
    font-style: italic;
}

/* Newsletter Empty State (when feature flag is NONACTIVE) */
.newsletter-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: #f9fafb;
    border-radius: 12px;
    border: 1px dashed #d1d5db;
    text-align: center;
}

.newsletter-empty-state i {
    font-size: 32px;
    color: #d1d5db;
    margin-bottom: 12px;
}

.newsletter-empty-state p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
}

.empty-feature-state {
    opacity: 0.7;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
}

.empty-feature-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
}

.empty-feature-content i {
    font-size: 24px;
    opacity: 0.5;
}
</style>
